/**
 * Wave 48 · Compose message modal.
 *
 * Two surfaces on the same 8-px grid:
 *   1. Recipients picker — tokenized multi-select. Typing "@" (optional)
 *      then any letter triggers a dropdown of matching users. Enter or
 *      tab adds the highlighted candidate; backspace on empty input
 *      removes the last token. Click an X on a token to drop it.
 *   2. Body — plain textarea with an @-mention autocomplete that
 *      triggers when the caret is immediately after an `@` followed by
 *      a handle-ish token. Picking a user replaces `@foo` with the
 *      canonical `@email-localpart`.
 *
 * Design choices (emil-design):
 *   - Dropdown is its own small card, arrows navigate, enter selects.
 *     transform-origin is anchored to the trigger via scale-in.
 *   - Debounce user search 120ms so rapid typing doesn't flood D1.
 *   - Submit stays disabled until at least 1 recipient + body > 0.
 *   - Enter submits; Shift+Enter wraps. Same rule as every chat app.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/api/client';
import { cn } from '@/lib/cn';
import { useT } from '@/hooks/useT';

export type UserHit = Awaited<ReturnType<typeof api.searchUsers>>[number];

const EASE = [0.22, 1, 0.36, 1] as const;

export function Compose({
  open,
  onClose,
  onSent,
  seedRecipient,
}: {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
  seedRecipient?: UserHit | null;
}) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const [recipients, setRecipients] = useState<UserHit[]>([]);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientHits, setRecipientHits] = useState<UserHit[]>([]);
  const [recipientIdx, setRecipientIdx] = useState(0);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyHits, setBodyHits] = useState<UserHit[]>([]);
  const [bodyHitIdx, setBodyHitIdx] = useState(0);
  const [bodyMentionRange, setBodyMentionRange] = useState<{ start: number; end: number } | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<Array<{ id: string; filename: string; sizeBytes: number; contentType: string }>>([]);
  const [uploading, setUploading] = useState(false);
  const recipientInputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    // Reset state each open. Seed a recipient if the caller passed one
    // (e.g., "reply to Anderson").
    setRecipients(seedRecipient ? [seedRecipient] : []);
    setRecipientQuery('');
    setRecipientHits([]);
    setSubject('');
    setBody('');
    setBodyHits([]);
    setBodyMentionRange(null);
    setError(null);
    setAttachments([]);
    // Focus the recipient field. A microtask delay lets the portal paint.
    queueMicrotask(() => recipientInputRef.current?.focus());
  }, [open, seedRecipient]);

  // ── Recipient autocomplete (debounced) ──────────────────────────
  useEffect(() => {
    if (!open) return;
    const q = recipientQuery.replace(/^@/, '').trim();
    if (q.length === 0) {
      setRecipientHits([]);
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        const hits = await api.searchUsers(q);
        // Drop users already in the recipients list.
        const picked = new Set(recipients.map((r) => r.id));
        setRecipientHits(hits.filter((h) => !picked.has(h.id)));
        setRecipientIdx(0);
      } catch {
        setRecipientHits([]);
      }
    }, 120);
    return () => window.clearTimeout(t);
  }, [recipientQuery, open, recipients]);

  // ── Body mention autocomplete ───────────────────────────────────
  //
  // Scan backward from the caret: if we find an `@` with only
  // [A-Za-z0-9._-] between it and caret, that's the active mention.
  const bodyOnChange = (value: string, caret: number) => {
    setBody(value);
    const prefix = value.slice(0, caret);
    const m = /@([A-Za-z0-9._-]{0,32})$/.exec(prefix);
    if (!m) {
      setBodyHits([]);
      setBodyMentionRange(null);
      return;
    }
    const start = caret - m[0]!.length;
    setBodyMentionRange({ start, end: caret });
    const q = (m[1] ?? '').trim();
    if (q.length === 0) {
      setBodyHits([]);
      return;
    }
    // Debounced: avoid firing for every keystroke while also staying
    // responsive. 120ms is the same as the recipient field.
    const t = window.setTimeout(async () => {
      try {
        const hits = await api.searchUsers(q);
        setBodyHits(hits.slice(0, 6));
        setBodyHitIdx(0);
      } catch {
        setBodyHits([]);
      }
    }, 120);
    return () => window.clearTimeout(t);
  };

  const acceptBodyHit = (hit: UserHit) => {
    if (!bodyMentionRange) return;
    const { start, end } = bodyMentionRange;
    const next = `${body.slice(0, start)}@${hit.handle} ${body.slice(end)}`;
    setBody(next);
    setBodyHits([]);
    setBodyMentionRange(null);
    // Put caret right after the inserted handle + space.
    queueMicrotask(() => {
      const pos = start + hit.handle.length + 2;
      bodyRef.current?.setSelectionRange(pos, pos);
      bodyRef.current?.focus();
    });
  };

  const canSend = recipients.length > 0 && body.trim().length > 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      await api.sendMessage({
        recipientUserIds: recipients.map((r) => r.id),
        body: body.trim(),
        subject: subject.trim() || null,
        attachmentIds: attachments.map((a) => a.id),
      });
      onSent();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : (isEn ? 'Failed to send' : 'Falha ao enviar'));
    } finally {
      setSending(false);
    }
  };

  const onFilesPicked = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      // Upload files in parallel — each file independent. R2 + D1 row
      // is created per file; attachmentIds[] flushes at send-time.
      const uploads = await Promise.all(
        Array.from(files).map((f) => api.uploadMessageAttachment(f)),
      );
      setAttachments((prev) => [...prev, ...uploads]);
    } catch (e) {
      setError(e instanceof Error ? e.message : (isEn ? 'Failed to upload attachment' : 'Falha ao subir anexo'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Esc to close. We intentionally don't close on outside click —
  // composing a long message and losing it would hurt.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[90] bg-ink/40 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
          />
          <motion.div
            role="dialog"
            aria-modal
            className={cn(
              'fixed z-[100] left-1/2 top-[10vh] w-[min(560px,calc(100vw-2rem))]',
              '-translate-x-1/2 rounded-2xl border border-line bg-surface shadow-elev-3',
            )}
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: EASE }}
          >
            <header className="flex items-center justify-between border-b border-line px-4 py-3">
              <p className="text-sm font-semibold text-ink">{isEn ? 'New message' : 'Nova mensagem'}</p>
              <button
                type="button"
                onClick={onClose}
                className="pressable rounded-lg px-2 py-1 text-sm text-ink-muted hover:text-ink"
                aria-label={isEn ? 'Close' : 'Fechar'}
              >
                ✕
              </button>
            </header>

            <div className="space-y-3 px-4 py-4">
              {/* Recipients row */}
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-ink-subtle">
                  {isEn ? 'To' : 'Para'}
                </label>
                <div
                  className={cn(
                    'mt-1 flex flex-wrap items-center gap-1.5 rounded-xl border border-line',
                    'bg-surface-2 px-2 py-1.5 focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-400/25',
                  )}
                >
                  {recipients.map((r) => (
                    <span
                      key={r.id}
                      className="inline-flex items-center gap-1 rounded-full bg-brand-400/15 px-2 py-0.5 text-[12px] font-medium text-brand-700 dark:text-brand-400"
                    >
                      {r.fullName}
                      <button
                        type="button"
                        onClick={() => setRecipients((prev) => prev.filter((x) => x.id !== r.id))}
                        className="text-brand-600 hover:text-ink"
                        aria-label={isEn ? `Remove ${r.fullName}` : `Remover ${r.fullName}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    ref={recipientInputRef}
                    value={recipientQuery}
                    placeholder={recipients.length === 0 ? (isEn ? 'Search by name or email…' : 'Busque por nome ou email…') : ''}
                    onChange={(e) => setRecipientQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && recipientQuery === '' && recipients.length > 0) {
                        setRecipients((prev) => prev.slice(0, -1));
                      } else if (e.key === 'Enter' || e.key === 'Tab') {
                        if (recipientHits[recipientIdx]) {
                          e.preventDefault();
                          setRecipients((prev) => [...prev, recipientHits[recipientIdx]!]);
                          setRecipientQuery('');
                          setRecipientHits([]);
                        }
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setRecipientIdx((i) => Math.min(recipientHits.length - 1, i + 1));
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setRecipientIdx((i) => Math.max(0, i - 1));
                      }
                    }}
                    className="min-w-[120px] flex-1 border-0 bg-transparent py-0.5 text-sm text-ink placeholder:text-ink-subtle focus:outline-none"
                  />
                </div>
                {recipientHits.length > 0 && (
                  <div
                    style={{ transformOrigin: 'top left' }}
                    className="mt-1 overflow-hidden rounded-xl border border-line bg-surface-2 shadow-pop dark:shadow-pop-dark animate-scale-in"
                  >
                    {recipientHits.map((h, i) => (
                      <button
                        type="button"
                        key={h.id}
                        onMouseEnter={() => setRecipientIdx(i)}
                        onClick={() => {
                          setRecipients((prev) => [...prev, h]);
                          setRecipientQuery('');
                          setRecipientHits([]);
                          recipientInputRef.current?.focus();
                        }}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                          i === recipientIdx ? 'bg-surface-3' : 'bg-transparent hover:bg-surface-3',
                        )}
                      >
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-400/20 text-[11px] font-semibold uppercase text-brand-700 dark:text-brand-400">
                          {initials(h.fullName)}
                        </span>
                        <span className="min-w-0 flex-1 truncate">
                          <span className="text-ink">{h.fullName}</span>
                          <span className="ml-2 text-[11px] text-ink-subtle">@{h.handle}</span>
                        </span>
                        <span className="shrink-0 text-[10px] uppercase tracking-wider text-ink-subtle">
                          {h.role}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Subject (optional) */}
              <div>
                <label className="block text-[11px] font-medium uppercase tracking-wider text-ink-subtle">
                  {isEn ? 'Subject (optional)' : 'Assunto (opcional)'}
                </label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={isEn ? "E.g.: Update on today's daily log" : "Ex: Avisando sobre o RDO de hoje"}
                  className="mt-1 w-full rounded-xl border border-line bg-surface-2 px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/25"
                />
              </div>

              {/* Body + @-mention autocomplete */}
              <div className="relative">
                <label className="block text-[11px] font-medium uppercase tracking-wider text-ink-subtle">
                  {isEn ? 'Message' : 'Mensagem'}
                </label>
                <textarea
                  ref={bodyRef}
                  value={body}
                  onChange={(e) => bodyOnChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
                  onKeyDown={(e) => {
                    if (bodyHits.length > 0) {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setBodyHitIdx((i) => Math.min(bodyHits.length - 1, i + 1));
                        return;
                      }
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setBodyHitIdx((i) => Math.max(0, i - 1));
                        return;
                      }
                      if (e.key === 'Enter' || e.key === 'Tab') {
                        e.preventDefault();
                        acceptBodyHit(bodyHits[bodyHitIdx]!);
                        return;
                      }
                      if (e.key === 'Escape') {
                        setBodyHits([]);
                        setBodyMentionRange(null);
                      }
                    }
                    if (e.key === 'Enter' && !e.shiftKey && !bodyHits.length) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  placeholder={isEn ? 'Write here… use @ to mention someone.' : 'Escreva aí… use @ pra mencionar alguém.'}
                  rows={5}
                  className="mt-1 w-full resize-none rounded-xl border border-line bg-surface-2 px-3 py-2 text-[14px] text-ink placeholder:text-ink-subtle focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/25"
                  style={{ minHeight: 120, maxHeight: 260 }}
                />
                {bodyHits.length > 0 && (
                  <div
                    style={{ transformOrigin: 'top left' }}
                    className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-xl border border-line bg-surface-2 shadow-pop dark:shadow-pop-dark animate-scale-in"
                  >
                    {bodyHits.map((h, i) => (
                      <button
                        type="button"
                        key={h.id}
                        onMouseEnter={() => setBodyHitIdx(i)}
                        onClick={() => acceptBodyHit(h)}
                        className={cn(
                          'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm',
                          i === bodyHitIdx ? 'bg-surface-3' : 'bg-transparent hover:bg-surface-3',
                        )}
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-400/20 text-[10px] font-semibold uppercase text-brand-700 dark:text-brand-400">
                          {initials(h.fullName)}
                        </span>
                        <span className="text-ink">{h.fullName}</span>
                        <span className="ml-2 text-[11px] text-ink-subtle">@{h.handle}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Attachment chips — only render the row when there's
                  something to show. Each chip has filename + size + an
                  X to remove pre-send (deletes the pending row from R2
                  via the cron purge; cheap and safe). */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {attachments.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2 px-2 py-1 text-[11px] text-ink"
                      title={`${a.filename} · ${formatBytes(a.sizeBytes)}`}
                    >
                      <span className="max-w-[180px] truncate">{a.filename}</span>
                      <span className="text-ink-subtle">{formatBytes(a.sizeBytes)}</span>
                      <button
                        type="button"
                        onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
                        className="text-ink-subtle hover:text-ink"
                        aria-label={isEn ? `Remove ${a.filename}` : `Remover ${a.filename}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {error && (
                <p role="alert" className="rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-xs text-err">
                  {error}
                </p>
              )}
            </div>

            <footer className="flex items-center justify-between border-t border-line px-4 py-3">
              <div className="flex items-center gap-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => void onFilesPicked(e.target.files)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  aria-label={isEn ? 'Attach file' : 'Anexar arquivo'}
                  className="pressable rounded-lg p-1.5 text-ink-muted hover:bg-surface-3 hover:text-ink disabled:opacity-50"
                  title={isEn ? 'Attach file (up to 25 MB each)' : 'Anexar arquivo (até 25 MB cada)'}
                >
                  <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path
                      d="M10.5 3.5l-5.3 5.3a2 2 0 1 0 2.83 2.83L13 6.66a3 3 0 0 0-4.24-4.24L3.5 7.66a4 4 0 0 0 5.66 5.66L13 9.5"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <p className="text-[11px] text-ink-subtle">
                  {uploading ? (isEn ? 'Uploading…' : 'Subindo…') : (isEn ? 'Enter sends · Shift+Enter wraps' : 'Enter envia · Shift+Enter quebra linha')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="pressable rounded-lg px-3 py-1.5 text-sm text-ink-muted hover:bg-surface-3 hover:text-ink"
                >
                  {isEn ? 'Cancel' : 'Cancelar'}
                </button>
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={!canSend}
                  className="pressable inline-flex h-9 items-center rounded-lg bg-ink px-4 text-sm font-medium text-surface hover:bg-ink/95 disabled:opacity-50"
                >
                  {sending ? (isEn ? 'Sending…' : 'Enviando…') : (isEn ? 'Send' : 'Enviar')}
                </button>
              </div>
            </footer>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
